// components/BookingSystem/BookingSystem.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Calendar from 'react-calendar';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCheckCircle, FaWhatsapp, FaUniversity, FaCopy, FaCalendarAlt, FaClock, FaUser, FaPhone, FaEnvelope, FaStickyNote } from 'react-icons/fa';
import {
  getDefaultBookingService,
  getServiceById,
  loadBookableServices,
} from '../../data/bookableServices';
import { isPagePricingService } from '../../config/pagePricingServices';
import PagePricingFlow from './PagePricingFlow';
import 'react-calendar/dist/Calendar.css';
import './css/BookingSystem.css';
import QRCode from '../images/qr-code.png';
import { API_BASE } from '../../config/api';
import { toDateOnlyString } from '../../utils/dateOnly';

/** Format slot time for display: "08:00 - 09:00" → "من 08:00 الى 09:00" */
const formatSlotTimeDisplay = (timeStr) => {
  if (!timeStr) return '';
  const match = String(timeStr).match(/^(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})$/);
  if (match) return `من ${match[1]} الى ${match[2]}`;
  return timeStr;
};

// Default time slots used as a fallback if backend config is not available.
const DEFAULT_TIME_SLOTS = {
  morning: [
    { time: '08:00 - 09:00', id: 'm1' },
    { time: '09:00 - 10:00', id: 'm2' },
    { time: '10:00 - 11:00', id: 'm3' },
    { time: '11:00 - 12:00', id: 'm4' },
  ],
  evening: [
    { time: '14:00 - 15:00', id: 'e1' },
    { time: '15:00 - 16:00', id: 'e2' },
    { time: '16:00 - 17:00', id: 'e3' },
    { time: '17:00 - 18:00', id: 'e4' },
  ],
};

// --- 1. Booking Calendar Component (uses selected service for title & prices) ---
const BookingCalendar = ({ service, onContinue }) => {
  const [date, setDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [reservedSlots, setReservedSlots] = useState([]);
  const [unavailableSlots, setUnavailableSlots] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [timeSlots, setTimeSlots] = useState(DEFAULT_TIME_SLOTS);

  const morningPrice = service.morningPrice ?? service.price ?? 150;
  const eveningPrice = service.eveningPrice ?? service.price ?? 200;

  // Load time slots configuration from backend so admin can control timelines.
  useEffect(() => {
    let isMounted = true;
    axios
      .get(`${API_BASE}/api/services/time-slots`)
      .then((res) => {
        if (!isMounted) return;
        if (res.data && res.data.timeSlots) {
          const incoming = res.data.timeSlots;
          setTimeSlots({
            morning: Array.isArray(incoming.morning) && incoming.morning.length
              ? incoming.morning
              : DEFAULT_TIME_SLOTS.morning,
            evening: Array.isArray(incoming.evening) && incoming.evening.length
              ? incoming.evening
              : DEFAULT_TIME_SLOTS.evening,
          });
        }
      })
      .catch(() => {
        // Fail silently, keep default slots
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const morningSlots = useMemo(
    () => (timeSlots.morning || DEFAULT_TIME_SLOTS.morning).map((s) => ({ ...s, price: morningPrice })),
    [timeSlots.morning, morningPrice]
  );
  const eveningSlots = useMemo(
    () => (timeSlots.evening || DEFAULT_TIME_SLOTS.evening).map((s) => ({ ...s, price: eveningPrice })),
    [timeSlots.evening, eveningPrice]
  );

  // Fetch availability from API when date changes
  useEffect(() => {
    const dateStr = toDateOnlyString(date);
    setLoadingAvailability(true);
    axios
      .get(`${API_BASE}/api/availability?date=${dateStr}`)
      .then((res) => {
        setReservedSlots(res.data.reservedSlots || []);
        setUnavailableSlots(res.data.unavailableSlots || []);
      })
      .catch(() => {
        setReservedSlots([]);
        setUnavailableSlots([]);
      })
      .finally(() => setLoadingAvailability(false));
  }, [date]);

  const blockedSlots = [...reservedSlots, ...unavailableSlots];

  const handleSlotClick = (slot) => {
    if (!blockedSlots.includes(slot.id)) {
      setSelectedSlot(slot);
    }
  };

  return (
    <motion.div 
      className="booking-step"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <h2 className="step-title">حجز موعد: {service.name}</h2>
      <p className="step-desc">{service.description}</p>
      <p className="step-desc step-duration">المدة: {service.duration}</p>

      <div className="calendar-layout">
        <div className="calendar-wrapper">
          <Calendar 
            onChange={setDate} 
            value={date} 
            locale="ar-EG"
            minDate={new Date()}
          />
        </div>

        <div className="slots-wrapper">
          <h4 className="date-header">
            {date.toLocaleDateString('ar-EG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </h4>

          <div className="session-group">
            <h5 className="session-title">☀️ الفترة الصباحية ({morningPrice} ريال)</h5>
            <div className="slots-grid">
              {morningSlots.map((slot) => {
                const isReserved = reservedSlots.includes(slot.id);
                const isUnavailable = unavailableSlots.includes(slot.id);
                const isBlocked = isReserved || isUnavailable;
                
                return (
                  <button
                    key={slot.id}
                    className={`slot-btn ${isReserved ? 'reserved' : ''} ${isUnavailable ? 'unavailable' : ''} ${selectedSlot?.id === slot.id ? 'selected' : ''}`}
                    onClick={() => handleSlotClick(slot)}
                    disabled={isBlocked}
                  >
                    <span className="slot-time">{formatSlotTimeDisplay(slot.time)}</span>
                    {isReserved ? (
                      <span className="slot-status">محجوز</span>
                    ) : isUnavailable ? (
                      <span className="slot-status">غير متاح</span>
                    ) : (
                      <span className="slot-price">{slot.price} ريال</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="session-group">
            <h5 className="session-title">🌙 الفترة المسائية ({eveningPrice} ريال)</h5>
            <div className="slots-grid">
              {eveningSlots.map((slot) => {
                const isReserved = reservedSlots.includes(slot.id);
                const isUnavailable = unavailableSlots.includes(slot.id);
                const isBlocked = isReserved || isUnavailable;
                
                return (
                  <button
                    key={slot.id}
                    className={`slot-btn ${isReserved ? 'reserved' : ''} ${isUnavailable ? 'unavailable' : ''} ${selectedSlot?.id === slot.id ? 'selected' : ''}`}
                    onClick={() => handleSlotClick(slot)}
                    disabled={isBlocked}
                  >
                    <span className="slot-time">{formatSlotTimeDisplay(slot.time)}</span>
                    {isReserved ? (
                      <span className="slot-status">محجوز</span>
                    ) : isUnavailable ? (
                      <span className="slot-status">غير متاح</span>
                    ) : (
                      <span className="slot-price">{slot.price} ريال</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <button 
            className="btn-primary-action" 
            disabled={!selectedSlot}
            onClick={() => onContinue(date, selectedSlot)}
          >
            متابعة للدفع ({selectedSlot ? selectedSlot.price : 0} ريال)
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Service options for "header of the services"
const SERVICE_OPTIONS = [
  { value: 'استشارة', label: 'استشارة' },
  { value: 'جلسة متابعة', label: 'جلسة متابعة' },
  { value: 'جلسة أسرية', label: 'جلسة أسرية' },
  { value: 'استشارة أولية', label: 'استشارة أولية' },
  { value: 'أخرى', label: 'أخرى' },
];

// --- 2. Checkout Page Component (UPDATED with QR & Bank Info + Guest Details) ---
const CheckoutPage = ({ bookingData, onConfirm, onBack }) => {
  const { date, slot, service } = bookingData;
  const serviceName = service?.name || 'استشارة';

  const [guest, setGuest] = useState({
    name: '',
    phone: '',
    email: '',
    serviceHeader: serviceName,
    notes: '',
  });

  const IBAN = "SA3105000068207060130000";
  const ACCOUNT_NUM = "68207060130000";

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("تم النسخ بنجاح");
  };

  const handleGuestChange = (field, value) => {
    setGuest((prev) => ({ ...prev, [field]: value }));
  };

  const canConfirm = guest.name.trim() && guest.phone.trim();

  return (
    <motion.div 
      className="booking-step"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <button className="btn-back" onClick={onBack}>← عودة للتقويم</button>
      <h2 className="step-title">إتمام الدفع</h2>

      <div className="checkout-layout">
        {/* Order Summary */}
        <div className="summary-card">
          <h3>ملخص الحجز</h3>
          {service && (
            <div className="summary-item summary-service">
              <span className="summary-service-name">{service.name}</span>
              {service.duration && <span className="summary-service-duration">المدة: {service.duration}</span>}
            </div>
          )}
          <div className="summary-item">
            <FaCalendarAlt className="icon" />
            <span>
              {date.toLocaleDateString('ar-EG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          </div>
          <div className="summary-item">
            <FaClock className="icon" />
            <span>{formatSlotTimeDisplay(slot.time)}</span>
          </div>
          <div className="summary-divider"></div>
          <div className="summary-total">
            <span>المجموع الكلي:</span>
            <span className="total-price">{slot.price} ريال سعودي</span>
          </div>
        </div>

        {/* Guest details form */}
        <div className="booking-details-card">
          <h3>بيانات الحجز</h3>
          <p className="booking-details-desc">يرجى إدخال بياناتك ليتم إضافتها للحجز والتقويم.</p>
          <div className="booking-details-form">
            <div className="form-group">
              <label><FaUser className="form-icon" /> الاسم</label>
              <input
                type="text"
                value={guest.name}
                onChange={(e) => handleGuestChange('name', e.target.value)}
                placeholder="الاسم الكامل"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label><FaPhone className="form-icon" /> رقم الجوال</label>
              <input
                type="tel"
                value={guest.phone}
                onChange={(e) => handleGuestChange('phone', e.target.value)}
                placeholder="05xxxxxxxx"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label><FaEnvelope className="form-icon" /> البريد الإلكتروني</label>
              <input
                type="email"
                value={guest.email}
                onChange={(e) => handleGuestChange('email', e.target.value)}
                placeholder="example@email.com"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>نوع الخدمة</label>
              <select
                value={guest.serviceHeader}
                onChange={(e) => handleGuestChange('serviceHeader', e.target.value)}
                className="form-input form-select"
              >
                {service ? (
                  <>
                    <option value={serviceName}>{serviceName}</option>
                    {SERVICE_OPTIONS.filter((opt) => opt.value !== serviceName).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </>
                ) : (
                  SERVICE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))
                )}
              </select>
            </div>
            <div className="form-group">
              <label><FaStickyNote className="form-icon" /> ملاحظات</label>
              <textarea
                value={guest.notes}
                onChange={(e) => handleGuestChange('notes', e.target.value)}
                placeholder="أي ملاحظات إضافية..."
                className="form-input form-textarea"
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Payment Details - UPDATED */}
        <div className="payment-card payment-card-full">
          <h3>بيانات التحويل البنكي</h3>
          <p className="payment-instruction">
          يُرجى التأكد من صحة المبلغ قبل التحويل، إذ لا يتم استرجاع المبلغ بعد الدفع.
          للاستفسارات: التواصل عبر الواتساب فقط،
          والأسعار ثابتة ولا يتم استقبال المكالمات
            </p>
          
          <div className="bank-details-container">
            {/* Left: Text Details */}
            <div className="bank-text-info">
              <div className="bank-header">
                <FaUniversity size={24} color="#402336" />
                <span className="bank-name">مصرف الإنماء</span>
              </div>
              
              <div className="info-row">
                <span className="label">اسم المستفيد:</span>
                <span className="value">نهى ناصر الحازمي</span>
              </div>

              <div className="info-row">
                <span className="label">رقم الحساب:</span>
                <div className="copy-wrapper">
                  <span className="value mono">{ACCOUNT_NUM}</span>
                  <button onClick={() => copyToClipboard(ACCOUNT_NUM)} className="btn-copy"><FaCopy /></button>
                </div>
              </div>

              <div className="info-row">
                <span className="label">رقم الآيبان:</span>
                <div className="copy-wrapper">
                  <span className="value mono">{IBAN}</span>
                  <button onClick={() => copyToClipboard(IBAN)} className="btn-copy"><FaCopy /></button>
                </div>
              </div>
            </div>

            {/* Right: QR Code Image */}
            <div className="bank-qr-display">
              <p className="qr-label">مسح الكود للدفع</p>
              {/* Ensure this image is in your public/assets folder */}
              <img 
                src={QRCode} 
                alt="Al Rajhi QR Code" 
                className="qr-img-file"
              />
            </div>
          </div>

          <button 
            className="btn-primary-action" 
            disabled={!canConfirm}
            onClick={() => onConfirm(guest)}
          >
            أتممت عملية الدفع
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// --- 3. Success Page Component (Unchanged) ---
const SuccessPage = () => {
  return (
    <motion.div 
      className="booking-step text-center"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
    >
      <div className="success-icon">
        <FaCheckCircle />
      </div>
      <h2 className="step-title">تم استلام طلبك بنجاح!</h2>
      <p className="success-message">
        شكرًا لثقتك، د. نهى الحازمي. <br />
        سنقوم بمراجعة عملية الدفع وتأكيد الحجز عبر الواتساب قريبًا.
      </p>
      
      <div className="success-actions">
        <a href="https://wa.me/966530494038" target="_blank" rel="noreferrer" className="btn-whatsapp">
          <FaWhatsapp /> متابعة الحالة عبر الواتساب
        </a>
        <button className="btn-home" onClick={() => window.location.reload()}>
          العودة للرئيسية
        </button>
      </div>
    </motion.div>
  );
};

// --- Main Container ---
const BookingSystem = () => {
  const location = useLocation();
  const navServiceId = location.state?.service?.id || null;

  const [service, setService] = useState(() =>
    location.state?.service?.id
      ? location.state.service
      : getDefaultBookingService()
  );
  const [serviceLoading, setServiceLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [bookingData, setBookingData] = useState(null);

  useEffect(() => {
    let isMounted = true;

    loadBookableServices()
      .then((list) => {
        if (!isMounted) return;
        const resolved = navServiceId
          ? getServiceById(navServiceId, list)
          : getDefaultBookingService(list);
        setService(resolved);
      })
      .finally(() => {
        if (isMounted) setServiceLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [navServiceId]);

  const handleDateSelection = (date, slot) => {
    setBookingData({ date, slot, service });
    setStep(2);
    window.scrollTo(0, 0);
  };

  const handlePaymentSuccess = async (guestDetails = {}) => {
    // Always proceed to success page regardless of calendar sync
    setStep(3);
    window.scrollTo(0, 0);

    // Attempt calendar sync in the background (non-blocking)
    if (bookingData) {
      try {
        const response = await axios.post(
          `${API_BASE}/api/booking`,
          {
            date: toDateOnlyString(bookingData.date),
            slot: bookingData.slot,
            service: bookingData.service
              ? {
                  id: bookingData.service.id,
                  name: bookingData.service.name,
                  description: bookingData.service.description || '',
                  duration: bookingData.service.duration || '',
                }
              : null,
            guest: {
              name: guestDetails.name || '',
              phone: guestDetails.phone || '',
              email: guestDetails.email || '',
              serviceHeader: guestDetails.serviceHeader || bookingData.service?.name || 'استشارة',
              notes: guestDetails.notes || '',
            },
          },
          {
            timeout: 5000, // 5 second timeout
          }
        );

        if (response.data && response.data.error) {
          console.warn('Calendar sync warning:', response.data.error);
          // Don't show alert - booking already succeeded
        } else {
          console.log('✅ Calendar event created successfully');
        }
      } catch (err) {
        // Network errors or server unavailable - silently handle
        if (err.code === 'ECONNABORTED' || err.message === 'Network Error' || !err.response) {
          console.warn('Calendar sync unavailable (server may be offline). Booking confirmed manually.');
        } else {
          console.warn('Calendar sync failed:', err?.response?.data?.error || err.message);
        }
        // Don't show alert - booking already succeeded, calendar sync is optional
      }
    }
  };

  if (serviceLoading) {
    return (
      <section className="booking-section" dir="rtl">
        <div className="container">
          <div className="text-center py-5">جاري تحميل الخدمة...</div>
        </div>
      </section>
    );
  }

  if (isPagePricingService(service)) {
    return (
      <section className="booking-section" dir="rtl">
        <div className="container">
          <PagePricingFlow service={service} />
        </div>
      </section>
    );
  }

  return (
    <section className="booking-section" dir="rtl">
      <div className="container">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <BookingCalendar
              key={`calendar-${service?.id}`}
              service={service}
              onContinue={handleDateSelection}
            />
          )}
          {step === 2 && (
            <CheckoutPage
              key="checkout"
              bookingData={bookingData}
              onConfirm={handlePaymentSuccess}
              onBack={() => setStep(1)}
            />
          )}
          {step === 3 && <SuccessPage key="success" />}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default BookingSystem;